import { makeAutoObservable, runInAction } from 'mobx';
import agent from '../api/agents';
import DateHelper from '../helpers/dateHelper';
import { FilterValues, LeaveBalanceFilterValues } from '../models/common/filterValues';
import { Department, Education, Employee, Holiday, JobStatus, LeaveBalance, Schedule, WorkExperience, EmployeeLeaveBalance } from '../models/employee';
import { DailyReport } from '../models/employeeDailyReport';
import { EmployeeFilterValues } from '../models/common/filterValues';
import { history } from '../../index';
import { toast } from 'react-toastify';
import { TimeRecord } from '../models/timeRecord';
import { OptionSelect } from '../models/option';
import { cl } from '@fullcalendar/core/internal-common';

const _dateHelper = new DateHelper();

export default class EmployeeStore {
    employees: Employee[] = [];
    allEmployees: Employee[] = [];
    employeesDepartment: Employee[] = [];
    departments: Department[] = [];
    total: number = 0;
    loadingInitial = false;
    isLoading = false;
    filterValues: EmployeeFilterValues = {
        pageNumber: 1, pageSize: 10, searchKeyword: "",
        departmentIds: []
    };

    //EmployeeDetails
    employee: Employee | undefined;
    workExperiences: WorkExperience[] | undefined;
    educationRecords: Education[] | undefined;
    jobStatus: JobStatus[] | undefined;
    schedule: Schedule | undefined;
    isEmployeeLoaded = false;

    holidays: Holiday[] | undefined;
    holidaysTotal: number = 0;
    isHolidayLoaded = false;

    leaveBalance: LeaveBalance[] | undefined;
    leaveBalanceTotal: number = 0;
    isLeaveBalanceLoaded = false;
    employeeLeaveBalances: EmployeeLeaveBalance[] = [];

    previousLeaveBalance: LeaveBalance[] | undefined;
    previousLeaveBalanceTotal: number = 0;
    isPreviousLeaveBalanceLoaded: boolean = false;

    searchingEmployee = false;
    searchedEmployees: Employee[] = [];

    dailyReports: DailyReport[] | undefined;
    dailyReportsTotal: number = 0;
    isDailyReportLoaded = false;

    timeRecords: TimeRecord[] | undefined;
    timeRecordTotal: number = 0;
    isTimeRecordsLoaded = false;
    constructor() {
        makeAutoObservable(this);
    }

    get pageNumber() {
        return this.filterValues.pageNumber ? this.filterValues.pageNumber - 1 : 0;
    }

    get pivotSchedule() {
        if (this.schedule) {
            return [{
                day: 'Monday',
                startTime: this.schedule.mondayWorkingDay ? _dateHelper.getTime(this.schedule.mondayStart) : '--',
                endTime: this.schedule.mondayWorkingDay ? _dateHelper.getTime(this.schedule.mondayEnd) : '--'
            }, {
                day: 'Tuesday',
                startTime: this.schedule.tuesdayWorkingDay ? _dateHelper.getTime(this.schedule.tuesdayStart) : '--',
                endTime: this.schedule.tuesdayWorkingDay ? _dateHelper.getTime(this.schedule.tuesdayEnd) : '--'
            }, {
                day: 'Wednesday',
                startTime: this.schedule.wednesdayWorkingDay ? _dateHelper.getTime(this.schedule.wednesdayStart) : '--',
                endTime: this.schedule.wednesdayWorkingDay ? _dateHelper.getTime(this.schedule.wednesdayEnd) : '--'
            }, {
                day: 'Thursday',
                startTime: this.schedule.thursdayWorkingDay ? _dateHelper.getTime(this.schedule.thursdayStart) : '--',
                endTime: this.schedule.thursdayWorkingDay ? _dateHelper.getTime(this.schedule.thursdayEnd) : '--'
            }, {
                day: 'Friday',
                startTime: this.schedule.fridayWorkingDay ? _dateHelper.getTime(this.schedule.fridayStart) : '--',
                endTime: this.schedule.fridayWorkingDay ? _dateHelper.getTime(this.schedule.fridayEnd) : '--'
            }, {
                day: 'Saturday',
                startTime: this.schedule.saturdayWorkingDay ? _dateHelper.getTime(this.schedule.saturdayStart) : '--',
                endTime: this.schedule.saturdayWorkingDay ? _dateHelper.getTime(this.schedule.saturdayEnd) : '--'
            }, {
                day: 'Sunday',
                startTime: this.schedule.sundayWorkingDay ? _dateHelper.getTime(this.schedule.sundayStart) : '--',
                endTime: this.schedule.sundayWorkingDay ? _dateHelper.getTime(this.schedule.sundayEnd) : '--'
            }];
        }

    }

    loadEmployees = async (filters: FilterValues) => {

        this.setLoading(true);
        try {
            console.log('loadEmployees');
            const { total, employees } = await agent.Employees.filter(filters);

            runInAction(() => {
                this.employees = employees;
                this.employeesDepartment = employees;

                this.total = total;
            });
            this.setLoading(false);
        } catch (error) {
            console.error(error);
            this.setLoading(false);
        }
    }

    getAllEmployees = async () => {
        this.setLoading(true);
        try {

            if (this.allEmployees.length === 0) {
                const { employees } = await agent.Employees.getAll();
                runInAction(() => {
                    this.allEmployees = employees;
                });
            }

            this.setLoading(false);
        } catch (error) {
            console.error(error);
            this.setLoading(false);
        }
    }

    loadAllDepartment = async () => {

        this.setLoading(true);
        try {
            const departments = await agent.Employees.getAllDepartment();

            runInAction(() => {
                this.departments = departments;
            });
            this.setLoading(false);
        } catch (error) {
            console.error(error);
            this.setLoading(false);
        }
    }


    loadEmployeesListByDepartment = async (filters: EmployeeFilterValues) => {
        this.setLoading(true);
        try {
            const { total, employees } = await agent.Employees.filterByDepartment(filters);
            runInAction(() => {

                this.employees = employees;
                this.total = total;
            });
            this.setLoading(false);
        } catch (error) {
            console.error(error);
            this.setLoading(false);
        }
    }


    get userOptionValues(): OptionSelect[] {
        return this.departments?.map(d => {
            return { value: d.id, label: d.name } as OptionSelect
        });
    }

    loadEmployee = async (userId: string) => {
        this.isEmployeeLoaded = false;
        try {
            const employeePersonalInfo = await agent.Employees.loadPersonalDetails(userId);
            const workExperiences = await agent.Employees.loadWorkExperiences(userId);
            const educationRecords = await agent.Employees.loadEducationRecords(userId);
            const employeeSchedule = await agent.Employees.loadSchedule(userId);
            const employeeJobStatus = await agent.Employees.loadJobStatus(userId);
            runInAction(() => {
                this.employee = employeePersonalInfo;
                this.workExperiences = workExperiences;
                this.educationRecords = educationRecords;
                this.schedule = employeeSchedule.schedule;
                this.jobStatus = employeeJobStatus;
                this.isEmployeeLoaded = true;
            });
        } catch (e) {
            this.isEmployeeLoaded = false;
            toast.error('Cannot load employee.');
            history.push('/employees');
        }
    }

    searchEmployee = async (search: string) => {
        this.searchingEmployee = true;

        try {
            const { total, employees } = await agent.Employees.filter({
                searchKeyword: search,
                pageSize: 0,
                pageNumber: 0
            });
            runInAction(() => {
                employees.map(e => {
                    if (!this.searchedEmployees.find(s => s.id == e.id)) {
                        this.searchedEmployees.push(e);
                    }
                });
                this.searchingEmployee = false;
            });
        } catch (error) {
            this.searchingEmployee = false;
        }
    }

    loadEmployeeDailyReports = async (filterValues: FilterValues) => {
        this.isDailyReportLoaded = false;
        try {
            const { total, data } = await agent.Employees.filterDailyReports({
                id: filterValues.id,
                pageNumber: filterValues.pageNumber,
                pageSize: filterValues.pageSize,
                searchKeyword: filterValues.searchKeyword
            });
            runInAction(() => {
                this.dailyReports = data;
                this.dailyReportsTotal = total;
                this.isDailyReportLoaded = true;
            });

        } catch (error) {
            console.error(error);
            this.isDailyReportLoaded = true;
        }
    }

    loadEmployeeHolidays = async (filterValues: FilterValues, userId: string) => {
        this.isHolidayLoaded = false;
        try {
            const { holidayScheduleId } = await agent.Employees.getHolidaySchedule(userId);
            const { total, holidays } = await agent.Employees.loadHolidays({
                pageNumber: filterValues.pageNumber,
                pageSize: filterValues.pageSize,
            }, holidayScheduleId);
            runInAction(() => {
                this.holidays = holidays;
                this.holidaysTotal = total;
                this.isHolidayLoaded = true;
            });

        } catch (error) {
            console.error(error);
            this.isHolidayLoaded = true;
        }
    }

    loadEmployeeLeaveBalance = async (filterValues: LeaveBalanceFilterValues, userId: string) => {
        this.isLeaveBalanceLoaded = false;

        try {
            const { total, leaveBalanceHistory } = await agent.Employees.loadLeaveBalance({
                pageNumber: filterValues.pageNumber,
                pageSize: filterValues.pageSize,
                leaveTypeId: filterValues.leaveTypeId
            }, userId);

            runInAction(() => {
                this.leaveBalance = leaveBalanceHistory;
                this.leaveBalanceTotal = total;
                this.isLeaveBalanceLoaded = true;
            });

        } catch (error) {
            console.error(error);
            this.isLeaveBalanceLoaded = true;
        }
    }

    loadPreviousLeaveBalance = async (filterValues: LeaveBalanceFilterValues, userId: string) => {
        this.isPreviousLeaveBalanceLoaded = false;

        try {
            const { total, leaveBalanceHistory } = await agent.Employees.getPreviousLeaveBalanceHistory({
                pageNumber: filterValues.pageNumber,
                pageSize: filterValues.pageSize,
            }, userId);

            runInAction(() => {
                this.previousLeaveBalance = leaveBalanceHistory;
                this.previousLeaveBalanceTotal = total;
                this.isPreviousLeaveBalanceLoaded = true;
            });

        } catch (error) {
            console.error(error);
            this.isPreviousLeaveBalanceLoaded = false;
        }
    }

    loadEmployeeDTR = async (filterValues: FilterValues) => {
        this.isTimeRecordsLoaded = false;
        try {
            const { total, data } = await agent.Employees.filterDTR(filterValues);
            runInAction(() => {
                this.timeRecords = data;
                this.timeRecordTotal = total;
                this.isTimeRecordsLoaded = true;
            });

        } catch (error) {
            console.error(error);
            this.isTimeRecordsLoaded = true;
        }
    }

    getLeaveBalances = async (userId: string) => {
        try {
            let data = await agent.Employees.getLeaveBalances(userId);

            runInAction(() => {
                this.employeeLeaveBalances = data
            });

        } catch (error) {
            console.error(error);
        }
    }

    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    }

    setLoading = (state: boolean) => {
        this.isLoading = state;
    }

    reset = () => {
        this.searchingEmployee = false;
        this.searchedEmployees = [];
    }

}